import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CoreService } from '../services/core.service';
import { Alert, eAppContext, FileModel } from '../models/global/class.global';
import { ParamsMedia, MediaQueovalModel, MediaExtranetModel, MediaModel, FilterMedia, UploadMediaModel, ParamsMediaModel } from '../models/media';
import { AlertCode, ConfigKey } from '../models/global/enum.global';
import { Observable, of } from 'rxjs';
import { HttpService } from './dataAccess.service';
import * as fileSaver from 'file-saver';
import { b64EncodeUnicode } from '../utilities/utilities.function';
import { map } from 'rxjs/operators';
import { ToastService } from '../toast';

@Injectable({
  providedIn: 'root'
})

export class MediaService {

  constructor(
    private http: HttpService,
    private indParam: CoreService,
    private httpclient: HttpClient,
    private toast: ToastService,
  ) { }

  /**
   * @description Récupération du média sur le serveur stream(retourne la destination) (stream api)
   * @param MediaName nom du média avec son extension
   */
  public getMedia(MediaName: string): Observable<ParamsMedia> {
    return this.http.getHttp<UploadMediaModel>(`${this.indParam.getConfigKey(ConfigKey.STREAM_HTTP)}api/MediaManager/GET_MEDIA/${MediaName}/`).pipe(
      map((result: UploadMediaModel) => {
        if (!result.HasError && !result.CodedAlerts.find((alert: Alert) => { alert.Code == AlertCode.BLOCKING_ERROR || alert.Code == AlertCode.ERROR }) && result.Media && result.Media.Media) {
          return result.Media.Media;
        } else {
          return new ParamsMedia();
        }
      }));
  }

  /**
   * @description Récupération de tout les médias sur le serveur stream par type (stream api)
   * @param typeMedias liste des types désirés séparés par un |
   *  VIDEO|IMG|AUDIO|DOC ou ALL
   *  @example 'VIDEO', 'IMG|VIDEO', 'DOC|VIDEO|AUDIO', 'ALL'
   */
  public GetMedias(typeMedias: string = 'ALL'): Observable<ParamsMedia[]> {
    return this.http.getHttp<UploadMediaModel>(`${this.indParam.getConfigKey(ConfigKey.STREAM_HTTP)}api/MediaManager/GET_ALL_MEDIAS_CLIENT/${typeMedias}/`).pipe(
      map((result: UploadMediaModel) => {
        if (!result.HasError && !result.CodedAlerts.find((alert: Alert) => { alert.Code == AlertCode.BLOCKING_ERROR || alert.Code == AlertCode.ERROR }) && result.Media && result.Media.ListMedia) {
          return result.Media.ListMedia;
        } else {
          return [];
        }
      }));
  }

  /**
   * @description récupération de tout les médias par rapport au filtre, en BDD (web api)
 * @param filter string: ident du media ou FilterMedia pour une liste filtée
 */
  public GetMediasFromDB(filter: FilterMedia | string = new FilterMedia, getFromEL: boolean = false): Observable<MediaQueovalModel> {
    const model: MediaQueovalModel = new MediaQueovalModel;
    if (typeof filter === 'string') {
      model.MediaModel.Filter = new FilterMedia;
      model.MediaModel.Filter.byName = filter;
    } else {
      model.MediaModel.Filter = filter;
    }
    model.Mode = 'GET_MEDIAS';
    let url = getFromEL ? this.indParam.AppParams.UrlElearningWS : this.indParam.AppParams.apiUrl;
    return this.http.postHttp<MediaQueovalModel>(`${url}api/media/`, model).pipe(
      map((result: MediaQueovalModel) => {
        if (!result.HasError && !result.CodedAlerts.find((alert: Alert) => { alert.Code == AlertCode.BLOCKING_ERROR || alert.Code == AlertCode.ERROR }) && result.MediaModel && result.MediaModel.ListMedia) {
          return result;
        } else {
          return new MediaQueovalModel();
        }
      }));
  }

  /**
   * @description supprime le média avec l'ident donné ainsi que les miniatures rattachées
   * quel que soit le dossier où il est stocké et quelle que soit son extension
   * puis retourne un booléen lié au succès de l'opération
   * @param ident
   */
  public deleteMedia(ident: string): Observable<boolean> {
    return this.http.getHttp<UploadMediaModel>(`${this.indParam.getConfigKey(ConfigKey.STREAM_HTTP)}api/MediaManager/DELETE_MEDIA/${ident}`).pipe(
      map((res: UploadMediaModel) => {
        return !res.HasError && res.CodedAlerts.filter((alert: Alert) => alert.Code === AlertCode.ERROR || alert.Code === AlertCode.BLOCKING_ERROR).length === 0
      }));
  }

  /**
* @description upload un média
* @param file fichier à uploader
* @param previews fichiers qui serviront de miniature
*/
  public uploadMedia(file: File, ident: string = '', previews: File[] | File = []): Observable<ParamsMediaModel> {
    if (!file) {
      this.toast.showAlertToast("Aucun fichier n'est sélectionné", 10000);
      return of(new ParamsMediaModel());
    }

    const config: any = {
      responseType: 'json' as 'json',
      observe: 'response'
    };
    const formData: FormData = new FormData();
    formData.append('', file, file.name);
    if (previews) {
      if ((previews as File).name) {
        previews = [(previews as File)];
      }
      (previews as File[]).forEach(f => formData.append('', f, f.name));
    }
    formData.append('token', this.indParam.AppParams.token);
    if (ident) {
      formData.append('ident', ident);
    }

    return this.httpclient.post<UploadMediaModel>(`${this.indParam.getConfigKey(ConfigKey.STREAM_HTTP)}api/UploadMedia/`, formData, config).pipe(
      map((res: any) => {
        return res?.body ?? new ParamsMediaModel();
      }));
  }

  /**
   * @description upload un média
   * @param file fichier à uploader
   * @param type types génériques VIDEO, IMG, AUDIO, DOC, LOGO, LOGO_CONNECT, BODISPLAY
   * sert à déterminer dans quel dossier se trouve le média à uploader
   * @param currentValue paramètre optionel pour le BODISPLAY
   * @example uploadMedia(videoFile, 'VIDEO')
   */
  public UploadLogo(file: File, type: string, currentValue?: string): any {
    localStorage.setItem('httpPath', '');
    const config: any = {
      responseType: 'arraybuffer' as 'arraybuffer',
      observe: 'response'
    };
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    formData.append('mode', type);
    formData.append('token', this.indParam.AppParams.token);
    if (currentValue) {
      formData.append('currentValue', currentValue);
    }
    let result: any = null;
    switch (this.indParam.Context) {
      case eAppContext.QUEOVAL:
        result = this.httpclient.post(`${this.getStreamHttp()}api/StreamUploadQueoval/`, formData, config);
        break;
      default:
        result = this.httpclient.post(`${this.getStreamHttp()}api/StreamUpload/`, formData, config);
        break;
    }
    const configFile: string = !this.indParam.getConfigKey(ConfigKey.STREAM_URL) || type === 'BODISPLAY' || type === 'LOGO' ? this.indParam.getConfigKey(ConfigKey.STREAM_HTTP) : this.indParam.getConfigKey(ConfigKey.STREAM_URL);
    localStorage.setItem('configFile', `${configFile}`);
    return result;
  }

  // ----------------- DOWNLOAD FILE -----------------
  /**
   * Téléchargement d'un fichier
   * @param idDoc Ident du document (ou nom du fichier)
   */
  //////downloadFile(fileModel: FileModel, openPdf: boolean = true): void {
  //////  this.indParam.showSplash(true);
  //////  // tslint:disable-next-line:radix
  //////  fileModel.Name = (fileModel.Name.indexOf('.') === -1 && !isNaN(parseInt(fileModel.Name))) ? `${fileModel.Name}.pdf` : fileModel.Name;


  //////  this.download(fileModel).subscribe((response: any) => {

  //////    // code modifié (avec response -> arraybuffer) ---------------------------------------------------------
  //////    let fileType: string = '';
  //////    switch (fileModel.Name.split('.').pop()) {
  //////      case 'pdf': fileType = 'application/pdf'; break;
  //////      case 'jpg': fileType = 'image/jpg'; break;
  //////      case 'jpeg': fileType = 'image/jpeg'; break;
  //////      case 'png': fileType = 'image/png'; break;
  //////      case 'gif': fileType = 'image/gif'; break;
  //////      case 'xml': fileType = 'application/xml'; break;
  //////      case 'txt': fileType = 'text/plain'; break;
  //////      case 'doc': fileType = 'application/vnd.ms-word'; break;
  //////      case 'docx': fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'; break;
  //////      case 'xls': fileType = 'application/vnd.ms-excel'; break;
  //////      case 'xlsx': fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; break;
  //////      case 'bmp': fileType = 'image/bmp'; break;
  //////      case 'wav': fileType = 'audio/wav'; break;
  //////      case 'ppt': fileType = 'application/vnd.ms-powerpoint'; break;
  //////      case 'pptx': fileType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'; break;
  //////      case 'dwg': fileType = 'image/vnd.dwg'; break;
  //////      case 'zip': fileType = 'octet/stream'; break;
  //////    }
  //////    const blob: Blob = new Blob([(response)], {
  //////      type: fileType,
  //////    });

  //////    const openWindow = fileType == 'application/pdf' && openPdf;

  //////    if (blob.size > 0) {
  //////      if (window.navigator && !openWindow) {
  //////        fileSaver.saveAs(blob, fileModel.Name);
  //////      } else {
  //////        // Pour le PDF on l'ouvre dans une nouvelle fenêtre
  //////        const objectUrl: string = URL.createObjectURL(blob);
  //////        window.open(objectUrl);
  //////      }

  //////    }
  //////    this.indParam.showSplash(false);

  //////    // --------------------------------------------------------------------------------
  //////  }, (error: any) => {
  //////    console.error((error) ? this.indParam.getTrad('Error_EL') : `${this.indParam.getTrad('undeferron_EL')} core.service.ts`);
  //////    this.indParam.showSplash(false);
  //////  });
  //////}

  /**
   * Appel serveur pour le téléchargement
   * @param fileName Nom du fichier à télécharger
   */
  //private download(fileModel: FileModel): Observable<any> {
  //  const data: any = { QueovalOriginalName: fileModel.Name, FilePath: fileModel.FullPath };
  //  const config: any = { responseType: 'arraybuffer' as 'arraybuffer' };
  //  // Download from streamService
  //  return this.httpclient.post(`${this.indParam.getConfigKey(ConfigKey.STREAM_HTTP)}api/StreamDownload`, data, config);
  //}


  public GetMediaEstore(fileName: string, authorKey: string): Observable<MediaQueovalModel> {
    const prm: string = b64EncodeUnicode(`${fileName}|${authorKey}`);
    return this.http.getHttp<MediaQueovalModel>(`${this.indParam.AppParams.apiUrl}api/media/GET_MEDIA_ESTORE/${prm}`);
  }

  /**
   * Fonction actuallement non utilisée mais à conserver
   * Permet de mettre à jour le champ Path_MED sur tous les médias (en cas de changement de serveur par exemple)
   * https://inscriptionformation.com/ -> https://toto.com
   * @param newServerName
   * @param listMediaIds liste customisée de médias à mettre à jour
   * @returns
   */
  public UpdateStoringServer(newServerName: string = '', listMediaIds: Array<number> = []): Observable<MediaQueovalModel> {

    newServerName = newServerName === '' ? this.getStreamHttp() : newServerName;

    const model: MediaQueovalModel = new MediaQueovalModel;
    model.MediaModel.ServerName = newServerName;
    model.MediaModel.ListMediaIds = listMediaIds;

    model.Mode = 'UPDATE_STORING_SERVER';
    return this.http.postHttp<MediaQueovalModel>(`${this.indParam.AppParams.apiUrl}api/media`, model);
  }

  private getStreamHttp(): string {
    return this.indParam.getConfigKey(ConfigKey.STREAM_HTTP);
  }
}
